import { ReactNode } from 'react';
import Link from 'next/link';
import { ChevronRight } from 'lucide-react';
import { ADMIN_TYPE } from './tokens';

interface Breadcrumb {
  label: string;
  href?: string;
}

interface AdminPageHeaderProps {
  title: ReactNode;
  description?: ReactNode;
  /** Trailing actions (buttons, switches, etc.). */
  actions?: ReactNode;
  /** Optional breadcrumb trail. The last item is rendered as plain text. */
  breadcrumbs?: Breadcrumb[];
  className?: string;
}

/**
 * Top-of-page header used on every admin route.
 *
 * Pattern (Linear / Notion / Stripe Dashboard):
 *  - Breadcrumbs as small overline
 *  - Title left-aligned, actions right-aligned
 *  - Description as secondary line under the title
 *  - Stacks gracefully on mobile (actions wrap below)
 */
export function AdminPageHeader({
  title,
  description,
  actions,
  breadcrumbs,
  className = '',
}: AdminPageHeaderProps) {
  return (
    <header className={`mb-6 lg:mb-8 ${className}`}>
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav
          aria-label="Breadcrumb"
          className="mb-3 flex flex-wrap items-center gap-1 text-xs text-gray-500"
        >
          {breadcrumbs.map((crumb, i) => {
            const isLast = i === breadcrumbs.length - 1;
            return (
              <span key={i} className="inline-flex items-center gap-1">
                {crumb.href && !isLast ? (
                  <Link
                    href={crumb.href}
                    className="rounded px-1 text-gray-600 transition-colors hover:bg-gray-100 hover:text-gray-950"
                  >
                    {crumb.label}
                  </Link>
                ) : (
                  <span className={isLast ? 'text-gray-900' : 'text-gray-600'}>
                    {crumb.label}
                  </span>
                )}
                {!isLast && (
                  <ChevronRight className="h-3 w-3 flex-shrink-0 text-gray-300" />
                )}
              </span>
            );
          })}
        </nav>
      )}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-6">
        <div className="min-w-0">
          <h1 className={`${ADMIN_TYPE.pageTitle} text-gray-950`}>{title}</h1>
          {description && (
            <p className="mt-1 max-w-2xl text-sm text-gray-600">{description}</p>
          )}
        </div>
        {actions && (
          <div className="flex flex-shrink-0 flex-wrap items-center gap-2">
            {actions}
          </div>
        )}
      </div>
    </header>
  );
}
